import {
  roundToDigits,
  unitDisplayLabel,
  type UnitLabelFormat,
  unitsInSystem,
  type UnitSystem,
} from '@onestep/camel-api';
import { isNumber, isUndefined } from 'lodash';

import type { ParameterConfig } from '@/types/measurements';

import { FixedDecimal } from '../mobx/number-class';

const unitSystemOf = (isImperial: boolean): UnitSystem => (isImperial ? 'imperial' : 'metric');

export const getParameterConfig = (parameterConfigs: ParameterConfig[], key: string) =>
  parameterConfigs.find((config) => config.key?.toLowerCase() === key?.toLowerCase()) ??
  ({
    roundDigits: 0,
  } as ParameterConfig);

export const transformParameter = (
  parameterConfigs: ParameterConfig[],
  key: string,
  value: number,
  isImperial: boolean = false,
): number => {
  if (isUndefined(value)) return value;
  const { roundDigits = 0, fromMetric } = unitsInSystem(
    getParameterConfig(parameterConfigs, key),
    unitSystemOf(isImperial),
  );
  const roundedNumber = roundToDigits(fromMetric(value), roundDigits);
  const fixedNumber = new FixedDecimal(roundedNumber, roundDigits);
  return isNumber(fixedNumber) ? (fixedNumber as number) : roundedNumber;
};

export const transformParametersRecord = (
  parameterConfigs: ParameterConfig[],
  parameters: Partial<Record<string, number>>,
  isImperial: boolean = false,
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(parameters).map(([key, value]) => [
      key,
      transformParameter(parameterConfigs, key, value as number, isImperial),
    ]),
  );

export const getParameterUnit = (
  parameterConfigs: ParameterConfig[],
  key: string,
  defaultUnit: string = '',
  isImperial: boolean = false,
): string =>
  unitsInSystem(getParameterConfig(parameterConfigs, key), unitSystemOf(isImperial)).unit ||
  defaultUnit;

export const getParameterUnitLabel = (
  parameterConfigs: ParameterConfig[],
  key: string,
  defaultUnit: string = '',
  isImperial: boolean = false,
  format: UnitLabelFormat = 'short',
): string =>
  unitDisplayLabel(getParameterUnit(parameterConfigs, key, defaultUnit, isImperial), format) ?? '';
